// Data hook for FulfillmentInventoryPanel. Fetches the org's units +
// their intake-form status enrichment in a single call (server side
// joins them; see server/fulfillment-handlers/unitsList.ts).

import { useCallback, useEffect, useRef, useState } from "react";
import {
  listFulfillmentUnits,
  type FulfillmentUnit,
} from "@/lib/fulfillment/fulfillmentApi";

export type FulfillmentInventoryModel = {
  units: FulfillmentUnit[];
  busy: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

export function useFulfillmentInventory(): FulfillmentInventoryModel {
  const [units, setUnits] = useState<FulfillmentUnit[]>([]);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);

  const refresh = useCallback(async () => {
    if (inFlight.current) return inFlight.current;
    setBusy(true);
    setError(null);
    const p = (async () => {
      const r = await listFulfillmentUnits();
      if (!r.ok) {
        setError(r.error);
        setUnits([]);
        return;
      }
      setUnits(r.data.units);
    })()
      .catch((e) => {
        setError(e instanceof Error ? e.message : "Failed to load fulfillment inventory.");
      })
      .finally(() => {
        setBusy(false);
        inFlight.current = null;
      });
    inFlight.current = p;
    return p;
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { units, busy, error, refresh };
}
